import { useCallback, useEffect, useRef, useState } from 'react';
import { isJobQueued, parseJobCycles, resolveDisplayPercent, rewriteFoundCyclesLabel, deriveCyclePointer } from '../utils/jobProgressUtils';

const OPTIMISTIC_SECONDS = {
  batch_upload: 20 * 60,
  rerun_compliance: 18 * 60,
  rerun_merged_compliance: 18 * 60,
  rerun_section: 3 * 60,
};

function formatRemaining(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return s <= 5 ? 'Almost done' : `${s}s left`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m ${String(r).padStart(2, '0')}s left`;
  }
  return `${m}m ${String(r).padStart(2, '0')}s left`;
}

const INITIAL = {
  visible: false,
  percent: 0,
  remainingSeconds: null,
  estimatedSeconds: null,
  phaseLabel: '',
  queued: false,
  queuePosition: null,
  jobType: null,
  jobId: null,
  started: false,
  awaitingResults: false,
  resumed: false,
  cycleAnchor: null,
  cycleIndex: null,
  cycleTotal: null,
  cycles: null,
  // Pipeline phase from job_progress events (e.g. ocr, compliance, saving).
  phase: null,
};

/**
 * Floating progress state driven by SSE job events + optimistic start on rerun/upload.
 * Completion (100%) only when results_ready is true — not when pipeline alone finishes.
 */
export function useProcessingProgress() {
  const [state, setState] = useState(INITIAL);
  const serverRemainingRef = useRef(null);
  const tickRef = useRef(null);
  const hideTimerRef = useRef(null);

  const reset = useCallback(() => {
    serverRemainingRef.current = null;
    clearTimeout(hideTimerRef.current);
    setState(INITIAL);
  }, []);

  const hydrateFromSnapshot = useCallback((payload, { resumed = false } = {}) => {
    if (!payload) return;
    const status = String(payload.status || '').toLowerCase();
    const eventName = String(payload.event || '').toLowerCase();
    if (status === 'failed' || status === 'cancelled' || eventName === 'job_failed') {
      return;
    }
    const toNum = (v) => {
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const event = payload.event || 'job_progress';
    const rem = toNum(payload.remaining_seconds);
    const est = toNum(payload.estimated_seconds);
    const queued = isJobQueued(payload);
    const saving = event === 'pipeline_complete'
      || payload.phase === 'saving'
      || payload.status === 'saving'
      || payload.results_ready === false
      || payload.results_ready === 'false'
      || payload.results_ready === 'False';
    const started = !queued && event !== 'job_estimated';
    // Stale Redis message from estimate phase — don't show "waiting to start" once running.
    const rawMsg = String(payload.message || '');
    const staleEstimateMsg = /waiting to start/i.test(rawMsg);
    const phaseLabel = (staleEstimateMsg && started)
      ? (resumed ? 'Resuming compliance processing…' : 'Processing…')
      : (payload.message
        || (resumed ? 'Resuming compliance processing…' : (queued ? 'Waiting to start…' : 'Processing…')));

    if (rem != null) serverRemainingRef.current = rem;
    else if (est != null) serverRemainingRef.current = est;

    const floor = queued ? 2 : (started ? 5 : 2);
    const displayPct = resolveDisplayPercent(payload, floor);
    const pointer = deriveCyclePointer({
      ...payload,
      phase: payload.phase,
      message: phaseLabel,
      phaseLabel,
      percent: displayPct,
    });
    setState({
      visible: true,
      percent: Math.max(displayPct, floor),
      remainingSeconds: rem ?? est,
      estimatedSeconds: est,
      phaseLabel: pointer.phaseLabel || phaseLabel,
      queued,
      queuePosition: payload.queue_position ?? null,
      jobType: (payload.job_type || '').toLowerCase() || null,
      jobId: payload.job_id || null,
      started,
      awaitingResults: saving,
      resumed,
      cycleAnchor: pointer.cycleAnchor || payload.cycle_anchor || null,
      cycleIndex: pointer.cycleIndex ?? payload.cycle_index ?? null,
      cycleTotal: pointer.cycleTotal || payload.cycle_total || null,
      cycles: pointer.cycles.length ? pointer.cycles : (parseJobCycles(payload.cycles) ?? null),
      phase: payload.phase || null,
    });
  }, []);

  const markResultsReady = useCallback((message = 'Complete') => {
    serverRemainingRef.current = 0;
    setState((prev) => ({
      ...prev,
      awaitingResults: false,
      queued: false,
      started: true,
      percent: 100,
      remainingSeconds: 0,
      phaseLabel: message,
    }));
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => reset(), 1400);
  }, [reset]);

  const startOptimistic = useCallback((jobType = 'rerun_compliance', jobId = null, extras = null) => {
    const jt = (jobType || '').toLowerCase();
    const est = OPTIMISTIC_SECONDS[jt] || OPTIMISTIC_SECONDS.rerun_compliance;
    const opts = extras && typeof extras === 'object' && !Array.isArray(extras) ? extras : {};
    serverRemainingRef.current = est;
    setState({
      visible: true,
      percent: 2,
      remainingSeconds: est,
      estimatedSeconds: est,
      phaseLabel: 'Starting…',
      queued: false,
      queuePosition: null,
      jobType: jt,
      jobId,
      started: false,
      awaitingResults: false,
      resumed: false,
      cycleAnchor: opts.cycleAnchor ?? null,
      cycleIndex: opts.cycleIndex ?? null,
      cycleTotal: opts.cycleTotal ?? null,
      cycles: parseJobCycles(opts.cycles) ?? null,
    });
  }, []);

  const applyEvent = useCallback((payload) => {
    const event = payload?.event;
    if (!event || event === 'heartbeat') return;

    const jobId = payload.job_id || payload.jobId || null;

    if (event === 'job_queued' || event === 'job_queue_position') {
      const pos = payload.queue_position ?? null;
      const active = payload.active_parallel;
      const maxP = payload.max_parallel;
      let label = payload.message
        || (pos ? `Queued — position ${pos}` : 'Waiting for GPU…');
      if (active != null && maxP != null) {
        label = pos
          ? `Queued #${pos} — ${active}/${maxP} GPU slots in use`
          : `Waiting for GPU — ${active}/${maxP} slots in use`;
      }
      setState((prev) => ({
        ...prev,
        visible: true,
        jobId: jobId || prev.jobId,
        queued: true,
        started: false,
        queuePosition: pos ?? prev.queuePosition,
        phaseLabel: label,
        percent: Math.max(prev.percent, 2),
        remainingSeconds: prev.estimatedSeconds ?? prev.remainingSeconds,
      }));
      return;
    }

    if (event === 'job_estimated') {
      const est = Number(payload.estimated_seconds ?? payload.estimatedSeconds);
      const rem = Number(payload.remaining_seconds ?? payload.remainingSeconds);
      const estOk = Number.isFinite(est) ? est : null;
      const remOk = Number.isFinite(rem) ? rem : null;
      if (estOk != null) serverRemainingRef.current = estOk;
      else if (remOk != null) serverRemainingRef.current = remOk;
      const pages = Number(payload.total_pages);
      const pageHint = Number.isFinite(pages) && pages > 0 ? ` (${pages} pages)` : '';
      setState((prev) => ({
        ...prev,
        visible: true,
        jobId: jobId || prev.jobId,
        estimatedSeconds: estOk ?? prev.estimatedSeconds,
        remainingSeconds: remOk ?? estOk ?? prev.remainingSeconds,
        percent: Math.max(prev.percent, Number(payload.overall_pct) || 2),
        phaseLabel: payload.message || `Estimated${pageHint} — waiting to start…`,
        queued: prev.queued,
        started: false,
        jobType: payload.job_type || prev.jobType,
      }));
      return;
    }

    if (event === 'job_started') {
      const rem = Number(payload.remaining_seconds ?? payload.remainingSeconds);
      if (Number.isFinite(rem)) serverRemainingRef.current = rem;
      const rawMsg = String(payload.message || '');
      const label = /waiting to start/i.test(rawMsg)
        ? 'Processing started…'
        : (payload.message || 'Processing started…');
      setState((prev) => ({
        ...prev,
        visible: true,
        jobId: jobId || prev.jobId,
        queued: false,
        started: true,
        awaitingResults: false,
        resumed: false,
        queuePosition: null,
        phaseLabel: label,
        percent: Math.max(prev.percent, 5),
        remainingSeconds: serverRemainingRef.current ?? prev.remainingSeconds,
        jobType: payload.job_type || prev.jobType,
      }));
      return;
    }

    if (event === 'job_progress' || event === 'pipeline_complete') {
      const rem = payload.remaining_seconds ?? payload.remainingSeconds;
      if (rem != null && Number.isFinite(rem)) {
        serverRemainingRef.current = rem;
      }
      const saving = event === 'pipeline_complete'
        || payload.phase === 'saving'
        || payload.results_ready === false;
      const cap = saving ? 96 : 99;
      let label = payload.message;
      if (!label && payload.section_label && payload.total) {
        label = `Checking compliance — ${payload.section_label} (${payload.progress}/${payload.total})`;
      } else if (!label && payload.section && payload.total) {
        label = `Section ${payload.progress}/${payload.total}`;
      }
      if (saving && !label) {
        label = 'Saving your compliance report…';
      }
      setState((prev) => {
        const nextPhase = saving ? 'saving' : (payload.phase || prev.phase);
        const nextCycles = parseJobCycles(payload.cycles) ?? prev.cycles;
        const pointer = deriveCyclePointer({
          ...payload,
          ...prev,
          phase: nextPhase,
          message: label || prev.phaseLabel,
          phaseLabel: label || prev.phaseLabel,
          percent: prev.percent,
          cycles: nextCycles,
          cycle_anchor: payload.cycle_anchor ?? prev.cycleAnchor,
          cycle_index: payload.cycle_index ?? prev.cycleIndex,
          cycle_total: payload.cycle_total ?? prev.cycleTotal,
        });
        return {
          ...prev,
          visible: true,
          jobId: jobId || prev.jobId,
          queued: false,
          started: true,
          awaitingResults: saving,
          percent: saving
            ? Math.max(prev.percent, 92)
            : resolveDisplayPercent(payload, prev.percent, { cap }),
          remainingSeconds: (rem != null && Number.isFinite(rem)) ? rem : prev.remainingSeconds,
          estimatedSeconds: payload.estimated_seconds ?? prev.estimatedSeconds,
          phaseLabel: rewriteFoundCyclesLabel(
            label || prev.phaseLabel || 'Processing…',
            pointer.cycleIndex,
            pointer.cycleTotal,
          ),
          jobType: payload.job_type || prev.jobType,
          cycleAnchor: pointer.cycleAnchor || prev.cycleAnchor,
          cycleIndex: pointer.cycleIndex ?? prev.cycleIndex,
          cycleTotal: pointer.cycleTotal || prev.cycleTotal,
          cycles: pointer.cycles.length ? pointer.cycles : nextCycles,
          phase: nextPhase,
        };
      });
      return;
    }

    if (event === 'job_completed') {
      const ready = payload.results_ready == null
        || payload.results_ready === true
        || payload.results_ready === 'true'
        || payload.results_ready === 'True';
      if (!ready) {
        setState((prev) => ({
          ...prev,
          visible: true,
          jobId: jobId || prev.jobId,
          awaitingResults: true,
          percent: Math.max(prev.percent, 94),
          phaseLabel: payload.message || 'Saving results…',
        }));
        return;
      }
      markResultsReady(payload.message || 'Complete');
      return;
    }

    if (event === 'job_failed') {
      clearTimeout(hideTimerRef.current);
      setState({
        ...INITIAL,
        visible: true,
        phaseLabel: payload.error ? `Failed — ${String(payload.error).slice(0, 80)}` : 'Failed',
      });
      hideTimerRef.current = setTimeout(() => reset(), 2800);
    }
  }, [markResultsReady, reset]);

  useEffect(() => {
    if (!state.visible || state.queued || !state.started) return undefined;
    tickRef.current = setInterval(() => {
      setState((prev) => {
        if (!prev.visible || prev.queued || !prev.started || prev.remainingSeconds == null) return prev;
        const server = serverRemainingRef.current;
        let next = Math.max(0, prev.remainingSeconds - 1);
        if (server != null && Number.isFinite(server)) {
          next = Math.min(next, server);
        }
        return { ...prev, remainingSeconds: next };
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [state.visible, state.queued, state.started]);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  return {
    ...state,
    formatRemaining: () => formatRemaining(state.remainingSeconds),
    startOptimistic,
    applyEvent,
    hydrateFromSnapshot,
    markResultsReady,
    reset,
  };
}

export { formatRemaining };
